/**
 * Datos de uso de Rumbo (ver docs-vadym/uso/README.md).
 *
 * `registrarUso(tipo, datos)` apunta un evento (ya filtrado por
 * `./privacidad`) en una cola; cada ~30 s y al ocultar la página se manda al
 * servidor de sincronización (POST /rumbo/uso), que lo guarda en un JSONL
 * mensual. Solo se apunta con el permiso de la persona (aviso único
 * `PermisoUso` o el interruptor de Ajustes); sin él, o con el interruptor
 * apagado, no se apunta ni se envía nada y la cola se vacía.
 */
import { send } from '@actual-app/core/platform/client/connection';

import { versionRumbo } from '#components/settings/versionRumbo';

import { aJsonl, ColaUso, crearEvento, idAleatorio } from './cola';
import type { Almacen, DispositivoUso } from './cola';
import { limpiarMensajeError, normalizarRuta } from './privacidad';

/** Interruptor antiguo (hasta la v0.7): solo cuenta su «0» (apagado). */
const CLAVE_ACTIVADO = 'rumbo:uso:activado';
/** Respuesta de la persona: «si» o «no». Sin respuesta, no se apunta nada. */
const CLAVE_PERMISO = 'rumbo:uso:permiso';
/**
 * Versión del texto al que dijo «sí». Al cambiar lo que se apunta (v2: el
 * Puente de mando) quien dijo «sí» a una versión anterior vuelve a ver la
 * ventana una vez («He mejorado mis informes»); quien dijo «no», no.
 */
const CLAVE_PERMISO_VERSION = 'rumbo:uso:permiso-version';
export const VERSION_PERMISO = 2;
const CLAVE_COLA = 'rumbo:uso:cola';
const INTERVALO_MS = 30_000;
const MAX_ERRORES_SESION = 30;

const almacenLocal: Almacen = {
  leer: () => window.localStorage.getItem(CLAVE_COLA),
  escribir: texto => window.localStorage.setItem(CLAVE_COLA, texto),
  borrar: () => window.localStorage.removeItem(CLAVE_COLA),
};

const cola = new ColaUso(1000, almacenLocal);
const sesion = idAleatorio();
let toques = 0;
let erroresSesion = 0;
let iniciado = false;
let enviando = false;
let pantallaActual: string | null = null;
let desdePantalla = 0;
const servidor: { url: string | null; token: string | null } = {
  url: null,
  token: null,
};

export type PermisoUso = 'si' | 'no' | 'pendiente' | 'renovar';

/**
 * ¿Ha dado permiso en este dispositivo? Quien apagó el interruptor antiguo
 * cuenta como «no»; el resto, como pendiente hasta que conteste. Quien dijo
 * «sí» a un texto anterior queda en «renovar» (no se apunta hasta que vuelva
 * a contestar).
 */
export function permisoUso(): PermisoUso {
  try {
    const permiso = window.localStorage.getItem(CLAVE_PERMISO);
    if (permiso === 'si') {
      const version = Number(
        window.localStorage.getItem(CLAVE_PERMISO_VERSION) ?? 1,
      );
      return version >= VERSION_PERMISO ? 'si' : 'renovar';
    }
    if (permiso === 'no') {
      return permiso;
    }
    return window.localStorage.getItem(CLAVE_ACTIVADO) === '0'
      ? 'no'
      : 'pendiente';
  } catch {
    return 'no';
  }
}

/** ¿Hay que enseñarle la ventana del permiso (primera vez o texto nuevo)? */
export function faltaPermisoUso(): boolean {
  const permiso = permisoUso();
  return permiso === 'pendiente' || permiso === 'renovar';
}

/** ¿Se apuntan datos de uso en este dispositivo? Solo con permiso. */
export function usoActivado(): boolean {
  return permisoUso() === 'si';
}

/** Da o retira el permiso. Al retirarlo se tira lo pendiente sin enviarlo. */
export function setUsoActivado(activado: boolean) {
  try {
    window.localStorage.setItem(CLAVE_PERMISO, activado ? 'si' : 'no');
    window.localStorage.setItem(CLAVE_PERMISO_VERSION, String(VERSION_PERMISO));
    window.localStorage.removeItem(CLAVE_ACTIVADO);
  } catch {
    // sin almacenamiento: no se recuerda
  }
  if (!activado) {
    cola.vaciar();
    pantallaActual = null;
  } else if (iniciado && pantallaActual == null) {
    // Recién dado en esta sesión: se empieza desde la pantalla actual.
    registrarUso('inicio');
    marcarPantalla(window.location.pathname);
  }
}

function dispositivo(): DispositivoUso {
  const w = window as typeof window & {
    __presupuestoEscala?: number;
    __presupuestoZoom?: number;
  };
  const consulta = (q: string) => {
    try {
      return window.matchMedia?.(q).matches ?? false;
    } catch {
      return false;
    }
  };
  return {
    ancho: window.innerWidth,
    alto: window.innerHeight,
    tactil: consulta('(pointer: coarse)'),
    estrecho: window.innerWidth < 512,
    oscuro: consulta('(prefers-color-scheme: dark)'),
    tema: nombreTema(),
    escala: w.__presupuestoEscala ?? 1,
    zoom: w.__presupuestoZoom ?? 1,
    dpr: window.devicePixelRatio || 1,
    instalada: consulta('(display-mode: standalone)'),
    plataforma: plataforma(),
  };
}

/** Solo el tipo de aparato: «ios», «android» o «escritorio». */
function plataforma(): 'ios' | 'android' | 'escritorio' {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) {
    return 'android';
  }
  // El iPad con iPadOS se presenta como Mac, pero es táctil.
  if (
    /iphone|ipad|ipod/i.test(ua) ||
    (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
  ) {
    return 'ios';
  }
  return 'escritorio';
}

/** Tema de la app; solo nombres sencillos (un tema propio con nombre raro → «otro»). */
function nombreTema(): string | null {
  const tema = document
    .querySelector('[data-theme]')
    ?.getAttribute('data-theme');
  if (!tema) {
    return null;
  }
  return /^[a-z0-9_-]{1,30}$/i.test(tema) ? tema : 'otro';
}

/** Versión de Rumbo (la de Ajustes), no la del motor del que nació. */
function versionApp() {
  return versionRumbo(
    import.meta.env.REACT_APP_VERSION_RUMBO,
    import.meta.env.REACT_APP_COMMIT,
  );
}

/**
 * Apunta un evento. `datos` pasa por la lista blanca de `./privacidad`: lo
 * que no esté allí no se guarda. Nunca lanza.
 */
export function registrarUso(tipo: string, datos?: Record<string, unknown>) {
  if (!usoActivado()) {
    return;
  }
  try {
    const evento = crearEvento(tipo, datos, {
      sesion,
      disp: dispositivo(),
      app: versionApp(),
      toques,
    });
    if (evento) {
      cola.anadir(evento);
      toques = 0;
    }
  } catch {
    // el registro de uso nunca debe romper la app
  }
}

/** Pantalla actual (ya normalizada), o null si no se apunta nada. */
export function pantallaUso(): string | null {
  return pantallaActual;
}

/** Cambio de pantalla: apunta la nueva y el tiempo pasado en la anterior. */
export function marcarPantalla(pathname: string) {
  if (!usoActivado()) {
    return;
  }
  const pantalla = normalizarRuta(pathname);
  if (pantalla === pantallaActual) {
    return;
  }
  const ahora = performance.now();
  if (pantallaActual == null) {
    // Primera pantalla: tiempo desde que se abrió la página hasta aquí.
    registrarUso('carga', { pantalla, ms: ahora });
  }
  registrarUso('pantalla', {
    pantalla,
    anterior: pantallaActual ?? undefined,
    ms: pantallaActual == null ? undefined : ahora - desdePantalla,
  });
  pantallaActual = pantalla;
  desdePantalla = ahora;
}

async function refrescarServidor() {
  try {
    const { url, token } = await send('rumbo-uso-conexion');
    servidor.url = url;
    servidor.token = token;
  } catch {
    // sin servidor: se queda en la cola local
  }
}

/**
 * Manda un lote al servidor. `salida`: la página se oculta o se cierra; no
 * se espera a nada (usa la URL y el token ya conocidos) y se guarda la cola
 * antes por si el navegador corta la petición.
 */
export async function enviarUso({ salida = false } = {}) {
  if (!usoActivado()) {
    cola.vaciar();
    return;
  }
  if (enviando) {
    if (salida) {
      cola.guardar();
    }
    return;
  }
  if (!salida) {
    await refrescarServidor();
  }
  const { url, token } = servidor;
  const lote = cola.pendientes();
  if (!url || !token || lote.length === 0) {
    cola.guardar();
    return;
  }
  if (salida) {
    cola.guardar();
  }
  enviando = true;
  try {
    const respuesta = await fetch(`${url}/rumbo/uso`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'X-ACTUAL-TOKEN': token,
      },
      body: JSON.stringify({ eventos: lote }),
    });
    if (respuesta.ok) {
      cola.confirmar(lote);
    }
  } catch {
    // sin red: se reintenta en el siguiente ciclo
  } finally {
    enviando = false;
    cola.guardar();
  }
}

/**
 * Lo recogido de quien llama (servidor + lo pendiente en este dispositivo),
 * en JSONL. Con `todos`, quien administra el servidor baja el de todas las
 * personas.
 */
export async function descargarUso({ todos = false } = {}): Promise<string> {
  await enviarUso();
  if (!servidor.url) {
    await refrescarServidor();
  }
  let texto = '';
  const { url, token } = servidor;
  if (url && token) {
    const respuesta = await fetch(
      `${url}/rumbo/uso${todos ? '?todos=1' : ''}`,
      {
        headers: { 'X-ACTUAL-TOKEN': token },
      },
    );
    if (!respuesta.ok) {
      throw new Error('uso-descarga');
    }
    texto = await respuesta.text();
  }
  return texto + aJsonl(cola.todos());
}

/**
 * Llama al Puente de mando del servidor (/rumbo/puente…) con la sesión de
 * esta persona. Devuelve `data` o lanza con el motivo (`sin-servidor`,
 * `forbidden`, `too-many`…). No depende del permiso de los datos de uso.
 */
export async function pedirPuente<T>(
  ruta: string,
  opciones: { metodo?: 'GET' | 'POST'; cuerpo?: unknown } = {},
): Promise<T> {
  return pedirRumbo<T>(`/rumbo/puente${ruta}`, opciones);
}

/**
 * Llama a una ruta de Rumbo del servidor (/rumbo/…) con la sesión de esta
 * persona. Devuelve `data` o lanza con el motivo que diga el servidor.
 */
export async function pedirRumbo<T>(
  ruta: string,
  {
    metodo = 'GET',
    cuerpo,
  }: { metodo?: 'GET' | 'POST'; cuerpo?: unknown } = {},
): Promise<T> {
  if (!servidor.url || !servidor.token) {
    await refrescarServidor();
  }
  const { url, token } = servidor;
  if (!url || !token) {
    throw new Error('sin-servidor');
  }
  const respuesta = await fetch(`${url}${ruta}`, {
    method: metodo,
    headers: {
      'X-ACTUAL-TOKEN': token,
      ...(cuerpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
  });
  const json = (await respuesta.json().catch(() => null)) as {
    status?: string;
    data?: T;
    reason?: string;
  } | null;
  if (!respuesta.ok || json?.status !== 'ok') {
    throw new Error(json?.reason ?? `http-${respuesta.status}`);
  }
  return json.data as T;
}

/**
 * La foto de un aviso («Avisar de un problema aquí»), solo para el admin.
 * Devuelve una URL `blob:` para pintarla; quien la pide la libera con
 * `URL.revokeObjectURL` al dejar de verla.
 */
export async function pedirFotoPuente(id: string): Promise<string> {
  if (!servidor.url || !servidor.token) {
    await refrescarServidor();
  }
  const { url, token } = servidor;
  if (!url || !token) {
    throw new Error('sin-servidor');
  }
  const respuesta = await fetch(
    `${url}/rumbo/puente/peticion/${encodeURIComponent(id)}/foto`,
    { headers: { 'X-ACTUAL-TOKEN': token } },
  );
  if (!respuesta.ok) {
    throw new Error(`http-${respuesta.status}`);
  }
  return URL.createObjectURL(await respuesta.blob());
}

/** Versión de la app para lo que se manda al servidor (p. ej. «Algo no va»). */
export function versionUso(): string | null {
  const { version, commit } = versionApp();
  return version ?? commit;
}

/** Borra lo pendiente en este dispositivo y lo suyo guardado en el servidor. */
export async function borrarUso(): Promise<boolean> {
  cola.vaciar();
  await refrescarServidor();
  const { url, token } = servidor;
  if (!url || !token) {
    return true;
  }
  try {
    const respuesta = await fetch(`${url}/rumbo/uso`, {
      method: 'DELETE',
      headers: { 'X-ACTUAL-TOKEN': token },
    });
    return respuesta.ok;
  } catch {
    return false;
  }
}

function alError(e: ErrorEvent) {
  if (erroresSesion >= MAX_ERRORES_SESION) {
    return;
  }
  erroresSesion++;
  const archivo = (e.filename || '').split(/[/\\]/).pop()?.split('?')[0];
  registrarUso('error', {
    tipo: 'error',
    mensaje: e.message || '',
    fuente: archivo ? `${archivo}:${e.lineno ?? 0}:${e.colno ?? 0}` : undefined,
  });
}

function alRechazo(e: PromiseRejectionEvent) {
  if (erroresSesion >= MAX_ERRORES_SESION) {
    return;
  }
  erroresSesion++;
  const motivo: unknown = e.reason;
  const texto =
    motivo instanceof Error
      ? `${motivo.name}: ${motivo.message}`
      : typeof motivo === 'string'
        ? motivo
        : 'rechazo';
  registrarUso('error', {
    tipo: 'promesa',
    mensaje: limpiarMensajeError(texto),
  });
}

/** Arranca el envío periódico y las capturas globales. Una sola vez. */
export function iniciarUso() {
  if (iniciado || typeof window === 'undefined') {
    return;
  }
  iniciado = true;
  cola.cargar();
  if (!usoActivado()) {
    cola.vaciar();
  }

  document.addEventListener(
    'pointerdown',
    () => {
      toques++;
    },
    { capture: true, passive: true },
  );
  window.addEventListener('error', alError);
  window.addEventListener('unhandledrejection', alRechazo);

  document.addEventListener('visibilitychange', () => {
    if (!usoActivado() || pantallaActual == null) {
      return;
    }
    const ahora = performance.now();
    if (document.visibilityState === 'hidden') {
      registrarUso('oculta', {
        pantalla: pantallaActual,
        ms: ahora - desdePantalla,
      });
      void enviarUso({ salida: true });
    } else {
      registrarUso('visible', { pantalla: pantallaActual });
    }
    desdePantalla = ahora;
  });
  window.addEventListener('pagehide', () => {
    void enviarUso({ salida: true });
  });

  registrarUso('inicio');
  void enviarUso();
  window.setInterval(() => void enviarUso(), INTERVALO_MS);
}
